import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import API_BASE_URL from "../apiConfig";
import { useAppSelector } from "../store/hooks";
import { selectUser, selectIsLoggedIn } from "../store/userSlice";
import { useTheme } from "../ThemeContext";
import { useAuthModal } from "../contexts/AuthModalContext";
import {
  PRIMARY_PINK,
  PRIMARY_PINK_DARK,
  SUCCESS_GREEN,
  TRANSPARENT_PINK_05,
  BORDER_LIGHT,
} from "../constants";
import MKButton from "../components/MKButton";

// Initialize Stripe
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY 
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

function CheckoutForm({ event, quantity, chessRegistration, dndRegistration, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success`,
        },
        redirect: "if_required",
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        if (onError) onError(stripeError.message);
        return;
      }

      // If payment succeeded, confirm on backend
      if (paymentIntent && paymentIntent.status === "succeeded") {
        const response = await fetch(`${API_BASE_URL}/api/stripe/confirm-payment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            paymentIntentId: paymentIntent.id,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to confirm payment");
        }

        const result = await response.json();
        if (onSuccess) onSuccess(result);
      } else {
        setError("Payment was not completed successfully");
        setProcessing(false);
        if (onError) onError("Payment was not completed successfully");
      }
    } catch (err) {
      console.error("Payment error:", err);
      setError(err.message || "An error occurred during payment");
      setProcessing(false);
      if (onError) onError(err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 3 }}>
        <PaymentElement />
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <MKButton
        type="submit"
        variant="contained"
        fullWidth
        disabled={!stripe || processing}
        sx={{
          bgcolor: PRIMARY_PINK,
          color: "#ffffff",
          "&:hover": {
            bgcolor: PRIMARY_PINK_DARK,
          },
          "&:disabled": {
            bgcolor: "#ccc",
            color: "#666",
          },
        }}
      >
        {processing ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1, color: "#ffffff" }} />
            Processing...
          </>
        ) : (
          `Pay $${(parseFloat(event.ticket_price) * quantity).toFixed(2)} ${event.ticket_currency || "USD"}`
        )}
      </MKButton>
    </form>
  );
}

function CheckoutPage() {
  const { darkMode } = useTheme();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector(selectUser);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const { openSignIn } = useAuthModal();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState(null);
  
  // Get quantity, chess registration, and DnD registration from location state
  const quantity = location.state?.quantity || 1;
  const chessRegistration = location.state?.chessRegistration || null;
  const dndRegistration = location.state?.dndRegistration || null;

  useEffect(() => {
    if (!isLoggedIn) {
      openSignIn();
      navigate(eventId ? `/events/${eventId}` : "/", { replace: true });
      return;
    }

    const fetchEventAndCreatePayment = async () => {
      try {
        // Fetch event details
        const eventResponse = await fetch(`${API_BASE_URL}/api/events/${eventId}`, {
          credentials: "include",
        });

        if (!eventResponse.ok) {
          throw new Error("Failed to fetch event");
        }

        const eventData = await eventResponse.json();
        setEvent(eventData);

        const ticketLimit = eventData.ticket_quantity != null && eventData.ticket_quantity !== ""
          ? parseInt(eventData.ticket_quantity, 10)
          : null;
        const ticketsSold = parseInt(eventData.total_tickets_sold || 0, 10);
        const ticketsRemaining = ticketLimit ? Math.max(0, ticketLimit - ticketsSold) : null;
        if (ticketsRemaining !== null && (ticketsRemaining <= 0 || quantity > ticketsRemaining)) {
          throw new Error(
            ticketsRemaining <= 0
              ? "This event is sold out"
              : `Only ${ticketsRemaining} ticket${ticketsRemaining === 1 ? "" : "s"} remaining`
          );
        }

        // Create payment intent
        const requestBody = {
          eventId: eventId,
          quantity: quantity,
        };

        if (chessRegistration) {
          requestBody.chess_registration = chessRegistration;
        }

        if (dndRegistration) {
          requestBody.dnd_registration = dndRegistration;
        }

        const paymentResponse = await fetch(
          `${API_BASE_URL}/api/stripe/create-payment-intent`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(requestBody),
          }
        );

        if (!paymentResponse.ok) {
          const errorData = await paymentResponse.json();
          throw new Error(errorData.error || "Failed to create payment intent");
        }

        const { clientSecret } = await paymentResponse.json();
        setClientSecret(clientSecret);
      } catch (err) {
        console.error("Error:", err);
        setError(err.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchEventAndCreatePayment();
  }, [eventId, isLoggedIn, navigate, quantity, chessRegistration, dndRegistration]);

  const handlePaymentSuccess = (result) => {
    setPaymentSuccess(true);
    setConfirmationCode(result.confirmationCode);
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !clientSecret) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/events/${eventId}`)}
        >
          Back to Event
        </Button>
      </Container>
    );
  }

  if (paymentSuccess) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <CheckCircleIcon
              sx={{ fontSize: 64, color: SUCCESS_GREEN, mb: 2 }}
            />
            <Typography variant="h4" gutterBottom sx={{ color: SUCCESS_GREEN }}>
              Payment Successful!
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your ticket purchase has been confirmed.
            </Typography>
            {confirmationCode && (
              <Box
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: TRANSPARENT_PINK_05,
                  borderRadius: 1,
                  border: `1px solid ${BORDER_LIGHT}`,
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Confirmation Code
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: PRIMARY_PINK, letterSpacing: 2 }}
                >
                  {confirmationCode}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Please save this code for your records. A confirmation email has been sent to your email address.
                </Typography>
              </Box>
            )}
            <MKButton
              variant="contained"
              onClick={() => navigate(`/events/${eventId}`)}
              sx={{
                bgcolor: PRIMARY_PINK,
                color: "#ffffff",
                "&:hover": {
                  bgcolor: PRIMARY_PINK_DARK,
                },
              }}
            >
              Back to Event
            </MKButton>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const options = {
    clientSecret,
    appearance: {
      theme: darkMode ? "night" : "stripe",
    },
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/events/${eventId}`)}
        sx={{ mb: 3 }}
      >
        Back to Event
      </Button>

      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom sx={{ color: PRIMARY_PINK, mb: 3 }}>
            Checkout
          </Typography>

          {event && (
            <>
              <Box sx={{ mb: 3, p: 2, bgcolor: TRANSPARENT_PINK_05, borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {event.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {event.location && `Location: ${event.location}`}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {event.start_time &&
                    `Date: ${new Date(event.start_time).toLocaleString()}`}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body1">
                    {quantity} ticket{quantity > 1 ? "s" : ""} × ${parseFloat(event.ticket_price).toFixed(2)}
                  </Typography>
                  <Typography variant="body1">
                    ${(parseFloat(event.ticket_price) * quantity).toFixed(2)} {event.ticket_currency || "USD"}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h6" sx={{ color: PRIMARY_PINK }}>
                    ${(parseFloat(event.ticket_price) * quantity).toFixed(2)} {event.ticket_currency || "USD"}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {clientSecret && stripePromise ? (
            <Elements stripe={stripePromise} options={options}>
              <CheckoutForm
                event={event}
                quantity={quantity}
                chessRegistration={chessRegistration}
                dndRegistration={dndRegistration}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </Elements>
          ) : clientSecret ? (
            <Alert severity="error">
              Stripe is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY in your environment variables.
            </Alert>
          ) : null}
        </CardContent>
      </Card>
    </Container>
  );
}

export default CheckoutPage;
